import { useQuery } from "@tanstack/react-query";
import { iField } from "/app/src/models";
import { iFieldService } from "/app/src/services";
import { buildParams } from "/app/src/helpers";

interface OrderFieldsData {
  orderFields: iField[];
  orderLineFields: iField[];
  isOrderFieldsFetched: boolean;
  isLinesFieldsFetched: boolean;
}

/**
 * Custom hook to fetch order fields based on an integration ID.
 * @param integrationId - The ID of the integration for which to fetch order fields.
 * @returns An object containing the order fields data.
 */
export const useOrderFields = (
  integrationId: number,
  level = "both",
): OrderFieldsData => {
  const { data: orderFields, isLoading: isOrderFieldsLoading } = useQuery({
    queryKey: ["orderFields", integrationId],
    queryFn: () => {
      return iFieldService.getAll(
        buildParams({ integrationId, level: "order" }),
      );
    },
    initialData: { fields: [] },
    select: (data: { fields: iField[] }) => data.fields,
    cacheTime: 30000,
    enabled: level !== "line" && integrationId !== undefined,
  });

  const { data: orderLineFields, isLoading: isLinesFieldsLoading } = useQuery({
    queryKey: ["orderLineFields", integrationId],
    queryFn: () => {
      return iFieldService.getAll(
        buildParams({ integrationId, level: "line" }),
      );
    },
    initialData: { fields: [] },
    select: (data: { fields: iField[] }) => data.fields,
    cacheTime: 30000,
    enabled: level !== "order" && integrationId !== undefined,
  });

  return {
    orderFields,
    orderLineFields,
    isOrderFieldsFetched: !isOrderFieldsLoading,
    isLinesFieldsFetched: !isLinesFieldsLoading,
  };
};
